import React, { ReactNode } from "react";
import "i18next";
import { Resource } from "i18next";
import i18n from "./i18nInstance";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { I18nextProvider } from "react-i18next";

import en from "../assets/language/en/website2.website.json";
import { useEffect, useState } from "react";
import { get } from "lodash";
import { loadWebResources } from "./loadWebResources";
import { loadMobileResources } from "./loadMobileResources";
// import arAR from "./assets/language/ar-AR/website2.website.json";

const cacheBustingVersion = "v2.0.0";

// declare module "i18next" {
//   interface CustomTypeOptions {
//     defaultNS: "website2.website";

//     resources: {
//       "website2.website": typeof en;
//     };
//   }
// }

export const allLanguages = Object.keys({
  "ca-ES": "Català",
  "en-US": "English",
  "en-GB": "English (UK)",
  "es-MX": "Español (Latinoamerica)",
  "es-ES": "Español (España)",
  "de-DE": "Deutcsh",
  "fr-FR": "Français",
  "fr-CA": "Français (Canada)",
  "hi-IN": "हिंदी",
  "id-ID": "Bahasa Indonesia",
  "fil-PH": "Filipino",
  "pl-PL": "Polski",
  "pt-BR": "Português (Brasil)",
  "pt-PT": "Português (Portugal)",
  "tr-TR": "Türkçe",
  "th-TH": "ไทย",
  "ru-RU": "Русский",
  "uk-UA": "Українська",
  "ar-AR": "‏العربية",
  "ko-KR": "한국어",
  "zh-CN": "中文(简体)",
  "ja-JP": "日本語",
  "vi-VN": "Tiếng Việt",
});

const LANGUAGE_FALLBACKS = {
  //en is default fallback for all locales
  "en-GB": ["en-US"], // US English
  "ar-ar": ["en-US"], // Arabic
  "es-MX": ["es-ES"], // Mexican Spanish
  "es-ES": ["es-MX"], // Spanish Spanish
  "fr-CA": ["fr-FR"], // Canadian French
  "fr-FR": ["fr-CA"], // French French
  "zh-CN": ["zh-HK", "zh-TW"],
  "zh-TW": ["zh-HK", "zh-CN"],
  "zh-HK": ["zh-TW", "zh-CN"],
  "ca-ES": ["es-ES", "es-MX"], // Catalan
};

export const getI18n = async (resources: Resource, lng?: string) => {
  // .use(initReactI18next) // Pass i18n instance to react-i18next
  if (!i18n.isInitialized) {
    await i18n
      .use(HttpBackend) // Load translations using http-backend
      .use(LanguageDetector) // Detect user language
      .init({
        lng,
        resources,
        defaultNS: "website2.website",
        load: "currentOnly",
        supportedLngs: ["en", ...allLanguages],
        react: {
          useSuspense: false,
        },
        fallbackLng: LANGUAGE_FALLBACKS, // Fallback language if detection fails
        debug: false,
        interpolation: {
          escapeValue: false, // React already escapes values
        },
        detection: {
          order: ["path", "htmlTag", "cookie", "navigator"],
          lookupFromPathIndex: 0,
        },
        backend: {
          loadPath: "/language/{{lng}}/website2.website.json",
          addPath: "/language/{{lng}}/website2.website.json",
        },
      });
  } else {
    i18n.changeLanguage(lng);
  }

  return i18n;
};

const getResourcesByBuildType = (lng: string, buildType?: string) => {
  switch (buildType) {
    case "mobile":
      return loadMobileResources(lng);
    case "www":
      return loadWebResources(lng);
    default:
      return loadWebResources(lng);
  }
};

export const initBrowserI18n = async (lng: string, buildType?: string) => {
  const langResource = await getResourcesByBuildType(lng, buildType);

  const resources = {
    [lng]: {
      "website2.website": langResource,
    },
  };

  const instance = await getI18n(resources, lng);

  return instance;
};

const formatLocale = (lang: string) => {
  if (lang === "en") return "en";

  const parts = lang.split("-");

  return `${parts[0]}-${parts[1].toUpperCase()}`;
};

export const I18nGate = ({ children }: { children: ReactNode }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let lang = window.location.pathname.split("/")[1] || "en";

    lang = formatLocale(lang);

    initBrowserI18n(lang).then(() => setReady(true));
  }, []);

  if (!ready) return null; // Or a loading spinner

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
};
